import { filters } from 'fabric';

export type CollageFrameStyle = 'mat' | 'tape' | 'vignette' | 'glow';

export interface CollageTheme {
  id: string;
  name: string;
  /** Whether this theme's ground is dark — drives readable text-color choices elsewhere (e.g. a title band). */
  dark: boolean;
  /** Canvas background behind the photos. */
  canvasBackground: string;
  /** Structural decoration renderCollageCanvas.ts draws around each photo — this is what makes a theme feel real instead of a color swap. */
  frame: CollageFrameStyle;
  captionFontFamily: string;
  captionFontWeight: string | number;
  captionFontStyle: 'normal' | 'italic';
  captionColor: string;
  captionBg: string;
  /** Fresh filter instances per call — Fabric filter objects are cheap and meant to be applied per-image, not shared. */
  buildPhotoFilters: () => InstanceType<(typeof filters)[keyof typeof filters]>[];
  /**
   * CSS `filter` approximation of buildPhotoFilters(), used only for the
   * Arrange step's live DOM photo preview — a plain <img>, not Fabric.
   * Without this, a theme's only visible trace in the editor was the canvas
   * background peeking through slot gaps, which disappears entirely once
   * every slot is filled with a photo (reported as "the theme gets lost").
   * The real, precise render still happens via buildPhotoFilters() through
   * Fabric at Preview/export time — this is a cheap approximation only.
   */
  previewFilterCss: string;
}

/**
 * Four themes, each a real Fabric.js filter chain plus a structural frame
 * treatment — not a color swap. Confirmed against a reviewed interactive
 * mockup before finalizing this list (see project plan).
 */
export const COLLAGE_THEMES: CollageTheme[] = [
  {
    id: 'classic',
    name: 'Classic',
    dark: false,
    canvasBackground: '#fbfaf7',
    frame: 'mat',
    captionFontFamily: '-apple-system, Helvetica, Arial, sans-serif',
    captionFontWeight: 600,
    captionFontStyle: 'normal',
    captionColor: '#3f3d38',
    captionBg: 'transparent',
    buildPhotoFilters: () => [],
    previewFilterCss: 'none',
  },
  {
    id: 'heirloom',
    name: 'Heirloom',
    dark: false,
    canvasBackground: '#efe4cd',
    frame: 'tape',
    captionFontFamily: 'Georgia, Cambria, "Times New Roman", serif',
    captionFontWeight: 400,
    captionFontStyle: 'italic',
    captionColor: '#5b3f20',
    captionBg: 'rgba(239,228,205,0.9)',
    buildPhotoFilters: () => [
      new filters.Sepia(),
      new filters.Saturation({ saturation: 0.15 }),
      new filters.Contrast({ contrast: 0.05 }),
      new filters.Noise({ noise: 40 }),
    ],
    previewFilterCss: 'sepia(0.5) saturate(1.15) contrast(1.05)',
  },
  {
    id: 'noir',
    name: 'Noir',
    dark: true,
    canvasBackground: '#0d0d0d',
    frame: 'vignette',
    captionFontFamily: '-apple-system, Helvetica, Arial, sans-serif',
    captionFontWeight: 800,
    captionFontStyle: 'normal',
    captionColor: '#ffffff',
    captionBg: 'rgba(0,0,0,0.85)',
    buildPhotoFilters: () => [
      new filters.Grayscale(),
      new filters.Contrast({ contrast: 0.28 }),
      new filters.Brightness({ brightness: -0.08 }),
      new filters.Noise({ noise: 35 }),
    ],
    previewFilterCss: 'grayscale(1) contrast(1.28) brightness(0.92)',
  },
  {
    id: 'dreamy',
    name: 'Dreamy',
    dark: false,
    canvasBackground: '#f4e8f2',
    frame: 'glow',
    captionFontFamily: '-apple-system, Helvetica, Arial, sans-serif',
    captionFontWeight: 700,
    captionFontStyle: 'normal',
    captionColor: '#9d174d',
    captionBg: 'rgba(255,255,255,0.92)',
    buildPhotoFilters: () => [
      new filters.Saturation({ saturation: -0.08 }),
      new filters.Brightness({ brightness: 0.06 }),
      new filters.Contrast({ contrast: -0.04 }),
    ],
    previewFilterCss: 'saturate(0.92) brightness(1.06) contrast(0.96)',
  },
];

export const DEFAULT_THEME_ID = 'classic';

export function resolveTheme(id: string | null | undefined): CollageTheme {
  return COLLAGE_THEMES.find(t => t.id === id) ?? COLLAGE_THEMES.find(t => t.id === DEFAULT_THEME_ID)!;
}

/** Simple month -> theme lookup for "Auto-arrange" — not a model, just a seasonal bias. */
export function suggestThemeForMonths(months: number[]): CollageTheme {
  const isWinter = months.some(m => m === 11 || m === 0 || m === 1); // Dec, Jan, Feb (0-indexed)
  return resolveTheme(isWinter ? 'noir' : 'dreamy');
}
